import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { sortData } from '../components/SortSelect';
import { Button } from '../components/ui/button';
import { ExportDialog } from '../components/ExportDialog';
import { useExport } from '../hooks/useExport';
import { exportTableToPDF } from '../utils/html2pdfExport';
import * as XLSX from 'xlsx';

import { IHALE_EXPORT_COLUMNS, INITIAL_FORM_DATA, INITIAL_FILTERS, toISODate } from '../components/modules/ihaleler/constants';
import { IhaleFilters } from '../components/modules/ihaleler/IhaleFilters';
import { IhaleTable } from '../components/modules/ihaleler/IhaleTable';
import { IhaleFormDialog } from '../components/modules/ihaleler/IhaleFormDialog';
import { IhaleDetailDialog } from '../components/modules/ihaleler/IhaleDetailDialog';
import { computeNakdiIlerleme, migrateLegacyIhale } from '../components/modules/ihaleler/calc';
import { formatIhaleField } from '../components/modules/ihaleler/format';
import { exportIhaleDetayPDF } from '../utils/ihaleDetayPdf';
import { tesisTuruMatches, tesisTuruList } from '../utils/tesisTuru';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IhalelerPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [ihaleler, setIhaleler] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIhale, setSelectedIhale] = useState(null);
  const [formData, setFormData] = useState({...INITIAL_FORM_DATA});
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const { selectedColumns, setSelectedColumns, pdfPageSize, setPdfPageSize, toggleColumn, selectAll, clearAll } = useExport();
  const [selectedRows, setSelectedRows] = useState([]);
  const [filters, setFilters] = useState({...INITIAL_FILTERS});
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [sortKey, setSortKey] = useState('newest');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedIhaleDetail, setSelectedIhaleDetail] = useState(null);

  useEffect(() => {
    if (!user?.is_admin && !user?.can_access_finance_module && !user?.is_viewer) {
      navigate('/dashboard');
      return;
    }
    fetchIhaleler();
    fetchUsers();
  }, []);

  const fetchIhaleler = async () => {
    try {
      const response = await axios.get(`${API}/ihaleler`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        withCredentials: true,
      });
      // Eski kayıtlarda saklanan yanlış nakdi_ilerleme'yi canlı yeniden hesapla
      const recomputed = (response.data || []).map(i => ({
        ...i,
        nakdi_ilerleme: computeNakdiIlerleme(i) || i.nakdi_ilerleme || '',
      }));
      setIhaleler(recomputed);
    } catch (error) {
      console.error('İhaleler fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users-simple`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  // Filtre mantığı
  const applyFilters = (data) => {
    return data.filter(ihale => {
      if (filters.yil.length > 0 && !filters.yil.includes(ihale.yil?.toString())) return false;
      if (filters.ihale_no && !ihale.ihale_no?.toLowerCase().includes(filters.ihale_no.toLowerCase())) return false;
      if (filters.kayit_no && !ihale.kayit_no?.toLowerCase().includes(filters.kayit_no.toLowerCase())) return false;
      if (filters.isin_adi && !ihale.isin_adi?.toLowerCase().includes(filters.isin_adi.toLowerCase())) return false;
      if (filters.ilcesi.length > 0 && !filters.ilcesi.includes(ihale.ilcesi)) return false;
      if (filters.durumu.length > 0 && !filters.durumu.includes(ihale.durumu)) return false;
      if (filters.alim_turu.length > 0 && !filters.alim_turu.includes(ihale.alim_turu)) return false;
      if (!tesisTuruMatches(ihale.tesis_turu, filters.tesis_turu)) return false;
      if (filters.sozlesme_turu.length > 0 && !filters.sozlesme_turu.includes(ihale.sozlesme_turu)) return false;
      if (filters.ihale_usulu.length > 0 && !filters.ihale_usulu.includes(ihale.ihale_usulu)) return false;
      if (filters.ilgilenen_memur.length > 0 && !filters.ilgilenen_memur.includes(ihale.ilgilenen_memur)) return false;
      if (filters.ihaleyi_alan_firma && !(ihale.ihaleyi_alan_firma || '').toLowerCase().includes(filters.ihaleyi_alan_firma.toLowerCase())) return false;
      if (filters.isi_yapan_idare.length > 0 && !filters.isi_yapan_idare.includes(ihale.isi_yapan_idare)) return false;
      if (filters.odenek_durumu.length > 0 && !filters.odenek_durumu.includes(ihale.odenek_durumu)) return false;
      if (filters.odenek_tarihi_bas || filters.odenek_tarihi_bit) {
        const od = toISODate(ihale.odenek_tarihi);
        if (!od) return false;
        if (filters.odenek_tarihi_bas && od < filters.odenek_tarihi_bas) return false;
        if (filters.odenek_tarihi_bit && od > filters.odenek_tarihi_bit) return false;
      }
      if (filters.kesin_kabul_durumu.length > 0 && !filters.kesin_kabul_durumu.includes(ihale.kesin_kabul_durumu)) return false;
      if (filters.fiyat_farki.length > 0 && !filters.fiyat_farki.includes(ihale.fiyat_farki)) return false;
      if (filters.is_artisi_1.length > 0 && !filters.is_artisi_1.includes(ihale.is_artisi_1_var_mi)) return false;
      if (filters.aciklama && !(ihale.aciklama || '').toLowerCase().includes(filters.aciklama.toLowerCase())) return false;
      if (filters.kontrol_teskilati.length > 0) {
        const kt = ihale.kontrol_teskilati || [];
        if (!filters.kontrol_teskilati.some(id => kt.includes(id))) return false;
      }
      if (filters.yaklasik_maliyet_min) {
        const val = parseFloat((ihale.yaklasik_maliyet || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
        if (isNaN(val) || val < parseFloat(filters.yaklasik_maliyet_min)) return false;
      }
      if (filters.sozlesme_bedeli_min) {
        const val = parseFloat((ihale.sozlesme_bedeli || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
        if (isNaN(val) || val < parseFloat(filters.sozlesme_bedeli_min)) return false;
      }
      if (filters.hakedis_min) {
        const val = parseFloat((ihale.toplam_odenen_hakedis || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
        if (isNaN(val) || val < parseFloat(filters.hakedis_min)) return false;
      }
      return true;
    });
  };

  const activeFilterCount = Object.entries(filters).reduce((count, [, val]) => {
    if (Array.isArray(val)) return count + val.length;
    if (typeof val === 'string' && val) return count + 1;
    return count;
  }, 0);

  const resetFilters = () => setFilters({...INITIAL_FILTERS});

  const toggleFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value]
    }));
  };

  // Para formatı
  const displayMoney = (value) => {
    if (!value) return '';
    let str = String(value).trim();
    if (!str || str === 'None' || str === 'null') return '';
    const isNegative = str.startsWith('-');
    if (isNegative) str = str.substring(1);
    let cleaned = str.replace(/[^\d.,]/g, '');
    if (!cleaned) return isNegative ? '-' : '';
    cleaned = cleaned.replace(/\.(\d+)(\.0{4,})/g, '.$1');
    let numValue = 0;
    if (cleaned.includes(',')) {
      numValue = parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
    } else {
      const dotCount = (cleaned.match(/\./g) || []).length;
      if (dotCount > 1) {
        numValue = parseFloat(cleaned.replace(/\./g, ''));
      } else if (dotCount === 1) {
        const parts = cleaned.split('.');
        const afterDot = parts[1] || '';
        if (afterDot.length === 3 && /^\d{3}$/.test(afterDot)) {
          numValue = parseFloat(parts[0] + afterDot);
        } else {
          numValue = parseFloat(cleaned);
        }
      } else {
        numValue = parseFloat(cleaned);
      }
    }
    if (isNaN(numValue)) return '';
    const formatted = numValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (isNegative ? '-' : '') + formatted + ' TL';
  };

  // Form işlemleri
  const resetForm = () => {
    setFormData({...INITIAL_FORM_DATA});
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const cleanedData = { ...formData };
      const integerFields = ['isin_suresi', 'ilave_sure', 'teminat_suresi_ay'];
      integerFields.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === null || cleanedData[field] === undefined) {
          cleanedData[field] = null;
        } else {
          cleanedData[field] = parseInt(cleanedData[field]);
        }
      });
      if (editMode) {
        await axios.put(`${API}/ihaleler/${selectedIhale.id}`, cleanedData, { withCredentials: true });
        alert('İhale güncellendi!');
      } else {
        await axios.post(`${API}/ihaleler`, cleanedData, { withCredentials: true });
        alert('İhale oluşturuldu!');
      }
      setDialogOpen(false);
      resetForm();
      await fetchIhaleler();
    } catch (error) {
      if (error.response?.data?.detail?.includes('Bu İhale Zaten Kaydedildi') ||
          error.response?.data?.detail?.includes('İhale No zaten kayıtlı')) {
        alert('UYARI: Bu İhale Kayıt Numarası (İKN) zaten sistemde mevcut. Lütfen farklı bir numara girin.');
        return;
      }
      const errorMsg = error.response?.data?.detail
        || (typeof error.response?.data === 'string' ? error.response?.data : null)
        || error.message || 'Bilinmeyen hata';
      alert('Hata: ' + errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ihaleyi silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/ihaleler/${id}`, { withCredentials: true });
      alert('İhale silindi!');
      await fetchIhaleler();
    } catch (error) {
      alert('Hata: ' + error.message);
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setDialogOpen(true);
  };

  const openEditDialog = (ihale) => {
    setSelectedIhale(ihale);
    const migrated = migrateLegacyIhale(ihale);
    const clean = Object.fromEntries(Object.entries(migrated).map(([k, v]) => [k, v === null ? '' : v]));
    setFormData({ ...INITIAL_FORM_DATA, ...clean, tesis_turu: tesisTuruList(migrated.tesis_turu), kontrol_teskilati: Array.isArray(migrated.kontrol_teskilati) ? migrated.kontrol_teskilati : [] });
    setEditMode(true);
    setDialogOpen(true);
  };

  // Export fonksiyonları
  const exportPDF = async () => {
    if (selectedColumns.length === 0) { alert('Lütfen en az bir sütun seçin'); return; }
    const filteredData = applyFilters(ihaleler);
    let dataToExport = selectedRows.length > 0
      ? filteredData.filter(i => selectedRows.includes(i.id))
      : filteredData;
    if (dataToExport.length === 0) { alert('Dışa aktarılacak ihale yok'); return; }

    try {
      const columnMapping = {
        'ihale_no': 'No', 'kayit_no': 'Kayıt No', 'yil': 'Yıl', 'ihale_tarihi': 'Tarih',
        'ihale_usulu': 'Usul', 'alim_turu': 'Tür', 'tesis_turu': 'Tesis Türü', 'sozlesme_turu': 'Sözleşme Türü',
        'sozlesme_tarihi': 'Sözleşme Tarih', 'isin_adi': 'İş Adı', 'ilgilenen_memur': 'Memur',
        'ilcesi': 'İlçe', 'isi_yapan_idare': 'İdare', 'odenek_durumu': 'Ödenek', 'odenek_tarihi': 'Ödenek Tarihi', 'durumu': 'Durum',
        'ihaleyi_alan_firma': 'Firma', 'firma_iletisim_adresi': 'Firma İletişim',
        'yaklasik_maliyet': 'Maliyet', 'sozlesme_bedeli': 'Sözleşme',
        'is_artisi_1_var_mi': '1.Artış?', 'is_artis_tutari_1': '1.Tutar',
        'is_artis_orani_1': '1.Oran%', 'guncel_sozlesme_bedeli_1': '1.Güncel',
        'is_artisi_2_var_mi': '2.Artış?', 'is_artis_tutari_2': '2.Tutar',
        'is_artis_orani_2': '2.Oran%', 'guncel_sozlesme_bedeli_2': '2.Güncel',
        'is_artisi_3_var_mi': '3.Artış?', 'is_artis_tutari_3': '3.Tutar',
        'is_artis_orani_3': '3.Oran%', 'guncel_sozlesme_bedeli_3': '3.Güncel',
        'is_artisi_var_mi': 'Süre Uzatımı?',
        'ek_kesin_teminat': 'Ek Teminat', 'revize_is_programi': 'Revize Prog',
        'ilave_all_risk': 'All Risk', 'toplam_odenen_hakedis': 'Hakediş',
        'alt_yuklenici_firma': 'Alt Yük.', 'fiyat_farki': 'FF', 'fiyat_farki_tutari': 'FF Tutar',
        'yer_teslim_tarihi': 'Yer Teslim', 'isin_suresi': 'Süre(Gün)', 'bitis_tarihi': 'Bitiş',
        'ilave_sure': 'Uzatım(Gün)', 'sure_uzatimi': 'İlave Bitim',
        'gecici_kabul_tarihi': 'G.Kabul', 'teminat_suresi_ay': 'Teminat(Ay)',
        'kesin_kabul_tarihi': 'K.Kabul', 'kesin_teminat_mektubu': 'Teminat',
        'kesin_kabul_durumu': 'K.Durum', 'nakdi_ilerleme': 'Nakdi(%)',
        'fiziki_ilerleme': 'Fiziki(%)', 'kontrol_teskilati': 'Kontrol Teş.',
        'insaat': 'İnşaat', 'elektrik': 'Elektrik', 'mekanik': 'Mekanik',
        'ziraat': 'Ziraat', 'aciklama': 'Açıklama', 'enlem': 'Enlem', 'boylam': 'Boylam'
      };

      const headers = ['S.NO', ...selectedColumns.map(col => columnMapping[col] || col)];
      const rows = dataToExport.map((ihale, index) =>
        [index + 1, ...selectedColumns.map(col => formatIhaleField(col, ihale[col], { users, displayMoney }) || '-')]
      );

      const parasalSutunlar = ['yaklasik_maliyet', 'sozlesme_bedeli', 'toplam_odenen_hakedis',
        'is_artis_tutari', 'guncel_sozlesme_bedeli', 'fiyat_farki_tutari', 'kesin_teminat_mektubu'];
      const parasalVarMi = selectedColumns.some(col => parasalSutunlar.includes(col));
      let allRows = rows;
      if (parasalVarMi) {
        const toplamRow = ['TOPLAM'];
        selectedColumns.forEach(col => {
          if (parasalSutunlar.includes(col)) {
            let total = 0;
            dataToExport.forEach(i => {
              const val = parseFloat((i[col] || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
              if (!isNaN(val)) total += val;
            });
            toplamRow.push(total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL');
          } else {
            toplamRow.push('');
          }
        });
        allRows = [...rows, toplamRow];
      }

      await exportTableToPDF(headers, allRows, 'İhaleler', `ihaleler${selectedRows.length > 0 ? '-secili' : ''}.pdf`, {
        subtitle: `Kayıt: ${dataToExport.length}${selectedRows.length > 0 ? ' (Seçili)' : ''}`,
        hasTotalRow: parasalVarMi,
        pageSize: pdfPageSize
      });
      setPdfDialogOpen(false);
    } catch (error) {
      alert('PDF oluşturulurken hata: ' + error.message);
    }
  };

  const exportExcel = () => {
    if (selectedColumns.length === 0) { alert('Lütfen en az bir sütun seçin'); return; }
    const filteredData = applyFilters(ihaleler);
    let dataToExport = selectedRows.length > 0
      ? filteredData.filter(i => selectedRows.includes(i.id))
      : filteredData;
    if (dataToExport.length === 0) { alert('Dışa aktarılacak ihale yok'); return; }

    try {
      const columnLabels = {
        'ihale_no': 'İhale No', 'kayit_no': 'Kayıt No', 'yil': 'Yıl',
        'ihale_tarihi': 'İhale Tarihi', 'ihale_usulu': 'İhale Usulü',
        'alim_turu': 'Alım Türü', 'tesis_turu': 'Tesis Türü', 'sozlesme_turu': 'Sözleşme Türü',
        'sozlesme_tarihi': 'Sözleşme Tarihi', 'isin_adi': 'İşin Adı',
        'ilgilenen_memur': 'İlgilenen Memur', 'ilcesi': 'İlçe',
        'isi_yapan_idare': 'İdare', 'odenek_durumu': 'Ödenek', 'odenek_tarihi': 'Gelen Ödenek Tarihi', 'durumu': 'Durum',
        'ihaleyi_alan_firma': 'Firma', 'firma_iletisim_adresi': 'Firma İletişim Adresi',
        'yaklasik_maliyet': 'Yaklaşık Maliyet', 'sozlesme_bedeli': 'Sözleşme Bedeli',
        'is_artisi_1_var_mi': '1. İş Artışı Var mı', 'is_artis_tutari_1': '1. İş Artış Tutarı',
        'is_artis_orani_1': '1. İş Artış Oranı (%)', 'guncel_sozlesme_bedeli_1': '1. Güncel Sözleşme',
        'is_artisi_2_var_mi': '2. İş Artışı Var mı', 'is_artis_tutari_2': '2. İş Artış Tutarı',
        'is_artis_orani_2': '2. İş Artış Oranı (%)', 'guncel_sozlesme_bedeli_2': '2. Güncel Sözleşme',
        'is_artisi_3_var_mi': '3. İş Artışı Var mı', 'is_artis_tutari_3': '3. İş Artış Tutarı',
        'is_artis_orani_3': '3. İş Artış Oranı (%)', 'guncel_sozlesme_bedeli_3': '3. Güncel Sözleşme',
        'is_artisi_var_mi': 'İş Artışı/Süre Uzatımı Var mı',
        'ek_kesin_teminat': 'Ek Kesin Teminat', 'revize_is_programi': 'Revize İş Programı',
        'ilave_all_risk': 'İlave All Risk', 'toplam_odenen_hakedis': 'Hakediş',
        'alt_yuklenici_firma': 'Alt Yüklenici', 'fiyat_farki': 'Fiyat Farkı',
        'fiyat_farki_tutari': 'Fiyat Farkı Tutarı', 'yer_teslim_tarihi': 'Yer Teslim Tarihi',
        'isin_suresi': 'İşin Süresi (Gün)', 'bitis_tarihi': 'Bitiş Tarihi',
        'ilave_sure': 'Süre Uzatımı (Gün)', 'sure_uzatimi': 'İlave Süreli İş Bitim Tarihi',
        'gecici_kabul_tarihi': 'Geçici Kabul Tarihi', 'teminat_suresi_ay': 'Teminat Süresi (Ay)',
        'kesin_kabul_tarihi': 'Kesin Kabul Tarihi', 'kesin_teminat_mektubu': 'Kesin Teminat',
        'kesin_kabul_durumu': 'Kesin Kabul', 'nakdi_ilerleme': 'Nakdi İlerleme (%)',
        'fiziki_ilerleme': 'Fiziki İlerleme (%)', 'kontrol_teskilati': 'Kontrol Teşkilatı',
        'insaat': 'İnşaat', 'elektrik': 'Elektrik', 'mekanik': 'Mekanik', 'ziraat': 'Ziraat',
        'aciklama': 'Açıklama', 'enlem': 'Enlem', 'boylam': 'Boylam'
      };

      const excelData = dataToExport.map((ihale, index) => {
        const row = { 'S.NO': index + 1 };
        selectedColumns.forEach(col => {
          row[columnLabels[col] || col] = formatIhaleField(col, ihale[col], { users, displayMoney });
        });
        return row;
      });

      const parasalSutunlar = ['yaklasik_maliyet', 'sozlesme_bedeli', 'toplam_odenen_hakedis',
        'is_artis_tutari', 'guncel_sozlesme_bedeli', 'fiyat_farki_tutari'];
      const parasalVarMi = selectedColumns.some(col => parasalSutunlar.includes(col));
      if (parasalVarMi) {
        const totalsRow = { 'S.NO': 'TOPLAM' };
        selectedColumns.forEach(col => {
          if (parasalSutunlar.includes(col)) {
            let total = 0;
            dataToExport.forEach(i => {
              const val = parseFloat((i[col] || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
              if (!isNaN(val)) total += val;
            });
            totalsRow[columnLabels[col] || col] = total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL';
          } else {
            totalsRow[columnLabels[col] || col] = '';
          }
        });
        excelData.push(totalsRow);
      }

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [
        { wch: 8 }, { wch: 15 }, { wch: 8 }, { wch: 40 }, { wch: 15 },
        { wch: 20 }, { wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 18 }
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'İhaleler');
      XLSX.writeFile(workbook, `ihaleler${selectedRows.length > 0 ? '-secili' : ''}.xlsx`);
    } catch (error) {
      alert('Excel oluşturulurken hata: ' + error.message);
    }
  };

  const exportSingleIhalePDF = (ihale) => {
    try {
      exportIhaleDetayPDF(ihale, { users, displayMoney });
    } catch (error) {
      alert('PDF oluşturulurken hata: ' + error.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Filtrelenmiş ve sıralanmış veri
  const filteredData = sortData(applyFilters(ihaleler), sortKey, 'isin_adi', 'yaklasik_maliyet');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">İhaleler Modülü</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-gray-300 hover:text-white" data-testid="anasayfa-nav-button">Anasayfa</Button>
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-300 hover:text-white">Dashboard</Button>
              <Button onClick={() => navigate('/finance/odenek')} variant="ghost" className="text-gray-300 hover:text-white">Ödenek</Button>
              <Button onClick={() => navigate('/finance/hakedis')} variant="ghost" className="text-gray-300 hover:text-white">Hakediş</Button>
              <Button onClick={() => navigate('/finance/santiye-takip')} variant="ghost" className="text-gray-300 hover:text-white">Şantiye Takip</Button>
              <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300">Çıkış</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2" data-testid="ihaleler-page-title">İhaleler</h2>
            <p className="text-gray-400">İhale takip ve yönetim sistemi</p>
          </div>
          <div className="flex gap-3">
            {ihaleler.length > 0 && (
              <Button onClick={() => {
                setPdfDialogOpen(true);
                if (selectedColumns.length === 0) {
                  setSelectedColumns(['ihale_no', 'isin_adi', 'ilcesi', 'durumu', 'yaklasik_maliyet']);
                }
              }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="export-btn">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                PDF/Excel İndir
              </Button>
            )}
            {!user?.is_viewer && (
              <Button onClick={openCreateDialog} className="bg-green-600 hover:bg-green-700 text-white" data-testid="new-ihale-btn">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Yeni İhale Ekle
              </Button>
            )}
          </div>
        </div>

        {ihaleler.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400">Henüz ihale eklenmemiş</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <IhaleFilters
              filters={filters}
              setFilters={setFilters}
              toggleFilter={toggleFilter}
              resetFilters={resetFilters}
              filterPanelOpen={filterPanelOpen}
              setFilterPanelOpen={setFilterPanelOpen}
              activeFilterCount={activeFilterCount}
              ihaleler={ihaleler}
              users={users}
              sortKey={sortKey}
              setSortKey={setSortKey}
              filteredCount={filteredData.length}
            />

            <IhaleTable
              data={filteredData}
              ihaleler={ihaleler}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              displayMoney={displayMoney}
              user={user}
              openEditDialog={openEditDialog}
              handleDelete={handleDelete}
              onRowClick={(ihale) => {
                setSelectedIhaleDetail(ihale);
                setDetailDialogOpen(true);
              }}
              onOpenExport={() => setPdfDialogOpen(true)}
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
            />
          </>
        )}
      </main>

      <IhaleFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editMode={editMode}
        formData={formData}
        setFormData={setFormData}
        users={users}
        displayMoney={displayMoney}
        onSubmit={handleSubmit}
        submitting={submitting}
        onCancel={() => { setDialogOpen(false); resetForm(); }}
      />

      <IhaleDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        ihale={selectedIhaleDetail}
        users={users}
        displayMoney={displayMoney}
        onEdit={openEditDialog}
        onExportPDF={exportSingleIhalePDF}
      />

      <ExportDialog
        open={pdfDialogOpen}
        onOpenChange={setPdfDialogOpen}
        columns={IHALE_EXPORT_COLUMNS}
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
        onSelectAll={() => selectAll(IHALE_EXPORT_COLUMNS)}
        onClearAll={clearAll}
        pdfPageSize={pdfPageSize}
        onPdfPageSizeChange={setPdfPageSize}
        onExportPDF={exportPDF}
        onExportExcel={exportExcel}
        quickSelect={{
          label: 'Hızlı Seçim (5 Temel)',
          onSelect: () => setSelectedColumns(['ihale_no', 'isin_adi', 'ilcesi', 'durumu', 'yaklasik_maliyet'])
        }}
        gridCols={4}
        idPrefix="ihale-export"
      />
    </div>
  );
};

export default IhalelerPage;
